/**
 * Les quatre cibles macros d'un formulaire — **un seul formulaire, deux
 * écrans**.
 *
 * Extrait de `screens/library/TemplateEditor.tsx` le 04/09/2026, quand le
 * journal a eu besoin des mêmes champs pour les objectifs d'un repas. Même
 * discipline que `ExerciseFacts` (étape 6) et que `shared/search.ts` : deux
 * implémentations de la même vue finissent par diverger, et le symptôme serait
 * qu'une cible de repas se saisisse **autrement selon l'endroit d'où on
 * l'ouvre** — un suffixe ici et pas là, la virgule acceptée d'un côté
 * seulement. Personne ne signale ce défaut : on se dit qu'on avait mal tapé.
 *
 * ⚠️ **Déplacé à l'identique**, commentaires compris. L'éditeur de modèles est
 * éprouvé sur l'appareil depuis l'étape 5 ; le refaire à l'occasion d'un ajout,
 * c'est le casser à l'occasion d'un ajout (leçon de `chartScale.ts`).
 *
 * ⚠️ **Les quatre macros restent indépendantes entre elles.** Rien ici ne dérive
 * `kcal` de P/G/L : l'écart 4/4/9 est affiché, en petit, à titre indicatif
 * (§5.3) — jamais imposé, jamais proposé comme correction.
 *
 * ⚠️ Les valeurs restent des **chaînes** tant que le formulaire est ouvert
 * (`lib/formFields.ts`) : un champ doit pouvoir être vidé ou contenir « 12. »
 * le temps d'une frappe.
 */

import type { MacroSet } from '../../../../shared/types';
import { gapOf, type MacroFields } from '../../lib/formFields';
import { NumberField } from './primitives';

/** Écart 4/4/9, affiché discrètement (§5.3). Ne propose rien. */
export function MacroGapNote({ macros }: { macros: MacroSet }) {
  const { theoretical, delta } = gapOf(macros);
  if (delta === 0) return null;
  return (
    <p className="n-hint">
      {delta > 0 ? '+' : '−'}
      {Math.abs(delta).toLocaleString('fr-FR')} kcal par rapport au 4/4/9 de P/G/L (
      {theoretical.toLocaleString('fr-FR')} kcal). Indicatif.
    </p>
  );
}

export function MacroFieldsRow({
  fields,
  onChange,
}: {
  fields: MacroFields;
  onChange: (next: MacroFields) => void;
}) {
  const set = (key: keyof MacroFields) => (value: string) => {
    onChange({ ...fields, [key]: value });
  };
  return (
    <div
      style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '0.4rem' }}
    >
      <NumberField label="Prot." suffix="g" value={fields.protein} onChange={set('protein')} />
      <NumberField label="Gluc." suffix="g" value={fields.carbs} onChange={set('carbs')} />
      <NumberField label="Lip." suffix="g" value={fields.fat} onChange={set('fat')} />
      <NumberField label="kcal" value={fields.kcal} onChange={set('kcal')} />
    </div>
  );
}
